import type { ReactNode } from "react";
import type { Key } from "react-aria-components";
import type { Size } from "../helpers/helpers.js";
import { SelectBase } from "./selectBase.js";

export interface SimpleSelectProps<T extends Key | null> {
  leftAccessory?: ReactNode;
  label?: string;
  isLabelHidden?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  /** Stretch the trigger button to fill its container instead of sizing to content. */
  isFullWidth?: boolean;
  placeholder?: ReactNode;
  /** Flat options; superseded by `sections` when both are provided. */
  options?: readonly SimpleSelectOption<T>[];
  /** Grouped options rendered with section headers. */
  sections?: readonly SimpleSelectSection<T>[];
  disabledValues?: readonly T[];
  value: T | null;
  onChange: (value: T) => void;
}

export interface SimpleSelectOption<T extends Key | null> {
  value: T;
  icon?: ReactNode;
  label: string;
}

export interface SimpleSelectSection<T extends Key | null> {
  id: string;
  /** Omitted when the section has no header. */
  label?: string;
  options: readonly SimpleSelectOption<T>[];
}

export const SimpleSelect = <T extends Key | null>(
  props: SimpleSelectProps<T>,
) => {
  const options =
    props.sections?.flatMap((section) => section.options) ??
    props.options ??
    [];
  return (
    <SelectBase
      {...props}
      options={options}
      renderOption={(option) => (
        <span className="flex-1 truncate">{option.label}</span>
      )}
    />
  );
};

export function getSimpleSelectOptionLabel<T extends Key | null>(
  options: readonly SimpleSelectOption<T>[],
  selectedId: T | undefined,
): string | undefined {
  if (selectedId === undefined) {
    return undefined;
  }

  const option = options.find((option) => option.value === selectedId);

  return option?.label;
}
